/**
 * A tab that was open across a deploy.
 *
 * Every page of the signed-in app is its own chunk with a content hash in its name, and a deploy
 * replaces the container, so the old names stop existing the moment the new build is up. A tab
 * opened before the deploy still holds the old list: the next page it navigates to asks for a file
 * that is gone, the import rejects, and the visitor is looking at an error screen for a page that
 * is perfectly healthy — once per open tab, after every deploy.
 *
 * Vite announces exactly this with `vite:preloadError`. The remedy is the one the visitor would
 * find by themselves: load the page again, which fetches the new shell and the new names. Once
 * only — if the reload did not cure it, the file is missing for some other reason, and reloading
 * forever would hide the error instead of showing it.
 */

const KEY = 'danak.staleBuildReload';
const ONCE_PER_MS = 60_000;

function reloadedRecently(now: number): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(KEY));
    return Number.isFinite(last) && last > 0 && now - last < ONCE_PER_MS;
  } catch {
    // Storage is blocked, so there is no way to tell a first failure from a loop. Not reloading is
    // the safe reading: the error boundary still explains itself, a reload loop does not.
    return true;
  }
}

/** Returns true when it started a reload. Exported for its test; call {@link watchForStaleBuild}. */
export function recoverFromStaleBuild(reload: () => void, now = Date.now()): boolean {
  if (reloadedRecently(now)) {
    return false;
  }

  try {
    window.sessionStorage.setItem(KEY, String(now));
  } catch {
    return false;
  }

  reload();
  return true;
}

export function watchForStaleBuild(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (recoverFromStaleBuild(() => window.location.reload())) {
      // Handled: without this Vite rethrows and the error boundary flashes up under the reload.
      event.preventDefault();
    }
  });
}
